<script lang="ts">
  import { brandStore } from '@/lib/brandStore.svelte';
  import { session } from '@/lib/session.svelte';
  import { navigateTo } from '@/lib/router';

  const labels = $derived(brandStore.labels);
  const cfg = $derived(brandStore.config);
  const results = $derived(session.results);
  const vehicle = $derived(session.vehicle);

  function confidenceClass(conf: number): string {
    if (conf > 0.8) return 'high';
    if (conf >= 0.5) return 'medium';
    return 'low';
  }

  function matchConfidenceClass(sim: number): string {
    if (sim > 0.8) return 'high';
    if (sim >= 0.5) return 'medium';
    return 'low';
  }

  function formatPrice(value: number | undefined, symbol: string): string {
    if (value === undefined || value === null) return 'Consulte';
    return new Intl.NumberFormat(cfg?.pricing?.currency?.locale ?? 'pt-BR', {
      style: 'currency',
      currency: symbol,
    }).format(value);
  }

  const pixPrice = $derived((p: number | undefined) => {
    const pix = cfg?.pricing?.pixDiscount ?? 0.9;
    return p !== undefined ? p * pix : undefined;
  });

  const sortedMatches = $derived(() => {
    if (!results?.matches) return [];
    return [...results.matches].sort((a, b) => (b.similarity ?? 0) - (a.similarity ?? 0));
  });

  const sortedScores = $derived(() => {
    if (!results?.scores) return [];
    return Object.entries(results.scores).sort((a, b) => b[1] - a[1]);
  });

  const isTopScore = $derived((cat: string): boolean => {
    if (!results?.scores) return false;
    const entries = Object.entries(results.scores).sort((a, b) => b[1] - a[1]);
    return entries[0]?.[0] === cat;
  });

  const hasResults = $derived(results && results.matches && results.matches.length > 0);
</script>

<div id="results-screen" class="screen">
  {#if results}
    <div class="result-card glass-panel-elevated visible" id="result-card" aria-live="polite">
      <div class="result-header">
        <div class="result-icon" id="result-icon">
          {brandStore.categories.find(c => c.id === results.category)?.icon ?? ''}
        </div>
        <div class="result-meta">
          <div class="result-label">{labels?.resultLabel ?? 'Produto Identificado'}</div>
          <div class="result-name" id="result-name">
            {brandStore.translateCategory(results.category)}
          </div>
          <div class="confidence-badge {confidenceClass(results.confidence)}" id="result-confidence">
            {(results.confidence * 100).toFixed(0)}%
          </div>
        </div>
      </div>

      {#if Object.keys(results.scores).length > 0}
        <div class="scores-section" id="bar-section">
          <div class="scores-title">{labels?.scoresTitle ?? 'Matriz de Confianca'}</div>
          <div class="bar-list" id="bar-list">
            {#each sortedScores() as [cat, score], i}
              <div class="bar-item">
                <span class="bar-label">{brandStore.translateCategory(cat)}</span>
                <div class="bar-value">
                  <div class="bar-fill {i === 0 ? 'top' : ''}" style="width: {score * 100}%"></div>
                </div>
                <span class="bar-percent">{(score * 100).toFixed(0)}%</span>
              </div>
            {/each}
          </div>
        </div>
      {/if}
    </div>

    <div class="matches-section" id="matches-section">
      <div class="matches-title">
        {labels?.matchesTitle ?? 'Produtos Encontrados'}
        {#if hasResults}
          <span class="matches-count">{sortedMatches().length}</span>
        {/if}
      </div>

      {#if hasResults}
        <div id="matches-list">
          {#each sortedMatches() as match, i}
            <a class="match-card" href={match.sourceUrl ?? '#'} target="_blank" rel="noopener" data-match-index={i}>
              <div class="match-rank-badge">{i + 1}</div>
              {#if match.imageUrl}
                <img class="match-thumb" src={match.imageUrl} alt={match.name} />
              {:else}
                <div class="match-thumb-placeholder">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                    <rect x="3" y="3" width="18" height="18" rx="2"/>
                    <circle cx="8.5" cy="8.5" r="1.5"/>
                    <polyline points="21 15 16 10 5 21"/>
                  </svg>
                </div>
              {/if}
              <div class="match-info">
                <div class="match-name">{match.name}</div>
                {#if match.vehicleInfo && (match.vehicleInfo.make || match.vehicleInfo.model)}
                  <div class="match-vehicle">
                    {#if match.vehicleInfo.yearStart || match.vehicleInfo.yearEnd}
                      <span class="vehicle-years">
                        {match.vehicleInfo.yearStart ?? '??'}{match.vehicleInfo.yearEnd ? ` - ${match.vehicleInfo.yearEnd}` : ''}
                      </span>
                    {/if}
                    {#if match.vehicleInfo.make}
                      <span class="vehicle-makemodel">{match.vehicleInfo.make}{match.vehicleInfo.model ? ` ${match.vehicleInfo.model}` : ''}</span>
                    {/if}
                    {#if match.vehicleMatch && match.vehicleMatch !== 'no_vehicle' && match.vehicleMatch !== 'no_match'}
                      <span class="badge badge-compatible">{labels?.badgeCompatible ?? 'Compativel'}</span>
                    {/if}
                  </div>
                {:else if vehicle && (vehicle.make || vehicle.model)}
                  <div class="match-vehicle">
                    <span class="vehicle-user">{vehicle.make} {vehicle.model}{vehicle.year ? ` ${vehicle.year}` : ''}</span>
                    <span class="badge badge-compatible">{labels?.badgeCompatible ?? 'Compativel'}</span>
                  </div>
                {/if}
                {#if match.pricePix !== undefined}
                  <div class="match-price">
                    <span class="priceavista">{labels?.labelPrice ?? 'a vista'} {formatPrice(pixPrice(match.pricePix), cfg?.pricing?.currency?.symbol ?? 'R$')}</span>
                    {#if match.priceInstallment !== undefined}
                      <span class="priceparcela">{match.installmentCount ?? cfg?.pricing?.installments ?? 6}x {labels?.labelInstallment ?? 'x R$'} {formatPrice(match.priceInstallment, cfg?.pricing?.currency?.symbol ?? 'R$')}</span>
                    {/if}
                  </div>
                {/if}
                {#if match.similarity !== undefined}
                  <div class="match-confidence {matchConfidenceClass(match.similarity)}">
                    <div class="confidence-bar-track">
                      <div class="confidence-bar-fill" style="width: {(match.similarity * 100).toFixed(0)}%"></div>
                    </div>
                    <span class="confidence-pct">{(match.similarity * 100).toFixed(0)}%</span>
                  </div>
                {/if}
              </div>
              <div class="match-actions">
                {#if match.sourceUrl}
                  <span class="match-link-btn">
                    {labels?.labelSourceAutoz ?? 'Ver na AutoZ'}
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                      <polyline points="15 3 21 3 21 9"/>
                      <line x1="10" y1="14" x2="21" y2="3"/>
                    </svg>
                  </span>
                {/if}
              </div>
            </a>
          {/each}
        </div>
      {:else}
        <div class="empty-state" id="empty-matches">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="8"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            <line x1="8" y1="11" x2="14" y2="11"/>
          </svg>
          <p>Nenhum produto encontrado para esta categoria.</p>
          <p class="empty-subtitle">Tente enviar uma foto mais nitida ou outra categoria.</p>
        </div>
      {/if}
    </div>
  {:else}
    <div class="glass-panel card empty-state" id="no-results">
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="8" x2="12" y2="12"/>
        <line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
      <p>Nenhum resultado. Tente buscar novamente.</p>
    </div>
  {/if}

  <button class="btn btn-secondary" id="back-btn" style="width:100%;margin-top:16px" onclick={() => navigateTo('upload')}>
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M19 12H5M12 19l-7-7 7-7"/>
    </svg>
    {labels?.btnNewScan ?? 'Nova Busca'}
  </button>
</div>